import { NgIf, AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, TemplateRef, ViewChild } from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { ChipItem, PageToolbarComponent, ChipsFilterComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';

import { PageTableFilterComponent } from '../../../shared/components/page-table-filter/page-table-filter.component';
import { DeliveryLine, DeliveryTableLine } from '../../models/delivery.model';
import { loadDeliveriesRequest, upsertDeliveryRequest } from '../../store/delivery.actions';
import { getDeliveryLines } from '../../store/delivery.selectors';

@Component({
  selector: 'app-delivery-overview',
  templateUrl: './delivery-overview.component.html',
  styleUrls: ['./delivery-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    NgIf,
    ChipsFilterComponent,
    PageTableFilterComponent,
    MatSlideToggle,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DeliveryOverviewComponent implements AfterViewInit {
  locationChipItems$: Observable<ChipItem<number>[]> = this.store
    .select(UserSelectors.selectLocations)
    .pipe(
      map((userlocations) => {
        this.selectedLocationIds = userlocations.filter((l) => l.isSelected).map((l) => l.id);
        return userlocations.map((userlocation) => ({
          value: userlocation.id,
          name: userlocation.name,
          selected: userlocation.isSelected,
        }));
      }),
    );

  selectedLocationIds: number[];
  columns: TableColumn[] = [
    {
      id: 'deliveryDate',
      type: TableColumnTypes.DATE,
      label: this.localeService.translate('delivery.overview-table.deliveryDate'),
    },
    {
      id: 'deliveryNote',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.deliveryNote'),
    },
    {
      id: 'orderNr',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.orderNr'),
    },
    {
      id: 'productNr',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.productNr'),
    },
    {
      id: 'productName',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.productName'),
    },
    {
      id: 'amount',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.amount'),
    },
    {
      id: 'units',
      type: TableColumnTypes.STRING,
      label: this.localeService.translate('delivery.overview-table.units'),
    },
    {
      id: 'isActive',
      type: TableColumnTypes.TEMPLATE,
      label: this.localeService.translate('delivery.overview-table.isActive'),
    },
  ];

  public tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([]),
    columns: this.columns,
    actionColumn: false,
    selectColumn: false,
    pagination: true,
    scrolling: true,
    width: '1200px',
    wrapperWidth: '100%',
    activeSort: {
      id: 'deliveryDate',
      start: 'desc',
      disableClear: false,
    },
  };

  public deliveryLines$: Observable<DeliveryTableLine[]> = this.store.select(getDeliveryLines).pipe(
    skipWhile((deliveryLines) => deliveryLines === null),
    map((deliveryLines: DeliveryLine[]) => {
      let lines: DeliveryTableLine[] = [];
      if (deliveryLines) {
        lines = deliveryLines.map((line) => ({
          id: line.id,
          locationId: line.locationId,
          deliveryDate: line.deliveryDate,
          deliveryNote: line.externalDeliveryNoteId,
          orderNr: line.externalOrderId,
          productNr: line.externalProductId,
          productName: line.productTitle,
          amount: line.unitQuantity,
          units: line.unitVolumeType,
          isActive: line.isActive,
        }));
      }
      return lines;
    }),
  );

  @ViewChild('isActiveColumn') isActiveColumn: TemplateRef<unknown>;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
  ) {}

  ngAfterViewInit() {
    setTimeout(() => {
      const newConfig = { ...this.tableConfig };
      const columnActive = newConfig.columns.find((c) => c.id === 'isActive');
      columnActive.template = this.isActiveColumn;
      this.tableConfig = newConfig;
    });
  }

  onToggleActive(tableLine: DeliveryTableLine) {
    const lTableLine = { ...tableLine };
    lTableLine.isActive = !tableLine.isActive;
    this.store.dispatch(upsertDeliveryRequest({ tableLine: lTableLine }));
  }

  public loadData() {
    this.store.dispatch(loadDeliveriesRequest({ locationIds: this.selectedLocationIds }));
  }

  changeLocation(selectedInput: number | number[]) {
    if (selectedInput !== undefined) {
      if (selectedInput instanceof Number) {
        this.selectedLocationIds = [selectedInput as number];
      } else {
        this.selectedLocationIds = selectedInput as number[];
      }
    }
    this.store.dispatch(loadDeliveriesRequest({ locationIds: this.selectedLocationIds }));
  }
}
